import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SECURITY_ENROLLMENT_EVENT, SIGNUP_VERIFICATION_EVENT } from "./securityInterceptor";
import { loadSecurityStatus } from "./securityStatus";
import { isSecurityOpenPath, SECURITY_SETUP_PATH, SIGNUP_VERIFY_PATH } from "./securityPaths";
import { formatDate, useSecurityText } from "./useSecurityText";

const GRACE_NOTICE_KEY = "security:grace-notice";

/**
 * Candado de 2FA obligatorio. No pinta nada: vive junto a las rutas (como
 * `BillingGate`) y, en cada cambio de pantalla, si la política exige 2FA y la
 * persona no lo tiene, la lleva a su perfil → Configuración.
 *
 * Mientras la fecha límite no llega sólo avisa (una vez por sesión del
 * navegador). Es la mitad amable del candado: la que de verdad impide usar la
 * plataforma es el backend, que responde 403 `TWO_FACTOR_ENROLLMENT_REQUIRED` a
 * todo lo demás; ese 403 también cae aquí, por si la pantalla llegó tarde.
 */
export default function SecurityGate() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { lang, tr } = useSecurityText();

  useEffect(() => {
    if (isSecurityOpenPath(pathname)) return;
    if (!localStorage.getItem("token")) return;
    let active = true;
    void loadSecurityStatus().then((status) => {
      if (!active || !status) return;
      // Alta manual sin correo confirmado (CC-CONSOLE#1550): primero el código.
      if (status.signupVerification?.required && !status.signupVerification.emailVerified) {
        navigate(SIGNUP_VERIFY_PATH, { replace: true });
        return;
      }
      if (status.enrollment.state === "locked") {
        navigate(SECURITY_SETUP_PATH, { replace: true });
        return;
      }
      if (status.enrollment.state === "grace" && !sessionStorage.getItem(GRACE_NOTICE_KEY)) {
        sessionStorage.setItem(GRACE_NOTICE_KEY, "1");
        const deadline = formatDate(status.enrollment.deadline, lang);
        toast.warning(tr("Activa la verificación en dos pasos", "Turn on two-step verification"), {
          description: tr(
            `A partir del ${deadline} será obligatoria para entrar a la plataforma.`,
            `From ${deadline} it will be required to use the platform.`,
          ),
          duration: 12000,
          action: {
            label: tr("Activar", "Turn on"),
            onClick: () => navigate("/profile?tab=settings"),
          },
        });
      }
    });
    return () => {
      active = false;
    };
    // `tr` cambia de identidad en cada render; basta con el idioma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navigate, lang]);

  // El backend rechazó una petición por falta de 2FA: al perfil.
  useEffect(() => {
    const onRequired = () => {
      void loadSecurityStatus({ force: true });
      if (!isSecurityOpenPath(window.location.pathname)) navigate(SECURITY_SETUP_PATH, { replace: true });
    };
    window.addEventListener(SECURITY_ENROLLMENT_EVENT, onRequired);
    return () => window.removeEventListener(SECURITY_ENROLLMENT_EVENT, onRequired);
  }, [navigate]);

  // El backend respondió EMAIL_VERIFICATION_REQUIRED: a /verificar.
  useEffect(() => {
    const onVerify = () => {
      const path = window.location.pathname;
      if (path === SIGNUP_VERIFY_PATH || path.startsWith("/register")) return;
      navigate(SIGNUP_VERIFY_PATH, { replace: true });
    };
    window.addEventListener(SIGNUP_VERIFICATION_EVENT, onVerify);
    return () => window.removeEventListener(SIGNUP_VERIFICATION_EVENT, onVerify);
  }, [navigate]);

  return null;
}
